import {
  CustomButton,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomModal,
  CustomSelect,
  errorDialog,
  successDialog,
  Option,
  Text,
  useText,
} from '@src/components';
import { Alert, Form, Upload } from 'antd';
import modalClose from '@src/assets/icons/icon-close.svg';
import '@src/styles/companyInformation/generalCompanyInformation/formModal.scss';
import { turkishToLower } from '@src/utils/utils';
import React, { useCallback, useState, useEffect } from 'react';
import iconInfo from '@src/assets/icons/icon-info.svg';
import iconUpload from '@src/assets/icons/icon-upload-file.svg';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { useDispatch } from 'react-redux';
import { addCompanyInfoFiles } from '@src/store/slice/companyInfosSlice';

const FormModal = ({ modalVisible, handleModalVisible, modalType }) => {
  const [form] = Form.useForm();
  const isMobil = useMediaQuery({ query: '(max-width: 1015.98px)' });

  const dispatch = useDispatch();

  const [fileList, setFileList] = useState([]);
  const [deletedFileList, setDeletedFileList] = useState([]);
  const [formData, setFormData] = useState([]);

  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  const pleaseAddTradeRegistryAndTaxPlateFiles = useText('pleaseAddTradeRegistryAndTaxPlateFiles');
  const pleaseSelectDocumentTypeAndAddFile = useText('pleaseSelectDocumentTypeAndAddFile');
  const pleaseAddTradeRegistryFile = useText('pleaseAddTradeRegistryFile');
  const pleaseAddTaxPlateFile = useText('pleaseAddTaxPlateFile');
  const pleaseSelectValidFile = useText('pleaseSelectValidFile');
  const pleaseCheckFileFormatAndSize = useText('pleaseCheckFileFormatAndSize');
  const fileSizeTooLarge = useText('fileSizeTooLarge');
  const pleaseSelectDocumentType = useText('pleaseSelectDocumentType');
  
  useEffect(() => {
    form.setFieldsValue({
      receiverSurName: modalType?.value,
    });
  }, [modalType]);

  const handleClose = useCallback(() => {
    handleModalVisible(false);
    form.resetFields();
    setFileList([]);
    setFormData([]);
  }, [form, handleModalVisible]);

  const onSave = useCallback(async () => {
    try {
      const values = await form.validateFields();

      const documentTypeList = [];
      formData.forEach((item) => {
        return documentTypeList.push(item.documentType);
      });

      const filesList = [];
      fileList.forEach((item) => {
        return filesList.push(item);
      });

      if ((documentTypeList.length === 0 || filesList.length === 0) && modalType?.type === 3) {
        errorDialog({
          title: <Text t="error" />,
          message: pleaseSelectDocumentTypeAndAddFile,
        });
        return;
      }

      if (
        (documentTypeList.length === 0 || filesList.length === 0) &&
        modalType?.type === 1 &&
        modalType?.type !== 3 &&
        modalType?.type !== 2
      ) {
        errorDialog({
          title: <Text t="error" />,
          message: pleaseAddTradeRegistryAndTaxPlateFiles,
        });
        return;
      } else if (
        !documentTypeList.includes(3) &&
        !documentTypeList.includes(5) &&
        modalType?.type === 1
      ) {
        errorDialog({
          title: <Text t="error" />,
          message: pleaseSelectDocumentTypeAndAddFile,
        });
        return;
      } else if (
        !documentTypeList.includes(3) &&
        documentTypeList.includes(5) &&
        modalType?.type === 1
      ) {
        errorDialog({
          title: <Text t="error" />,
          message: pleaseAddTradeRegistryFile,
        });
        return;
      } else if (
        documentTypeList.includes(3) &&
        !documentTypeList.includes(5) &&
        modalType?.type === 1
      ) {
        errorDialog({
          title: <Text t="error" />,
          message: pleaseAddTaxPlateFile,
        });
        return;
      }

      const data = new FormData();

      data.append('CompanyInfoId', modalType?.companyInfoId);
      data.append('CompanyInfoFieldType', modalType?.fieldType);
      data.append('NewValue', values?.receiverSurName);

      fileList.forEach((item) => {
        return data.append('FormFiles', item?.originFileObj, item?.originFileObj?.name);
      });

      documentTypeList.forEach((item) => {
        return data.append('CompanyInfoTypes', item);
      });

      const action = await dispatch(addCompanyInfoFiles(data));

      if (addCompanyInfoFiles.fulfilled.match(action)) {
        successDialog({
          title: <Text t="success" />,
          message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
          onOk: () => { },
        });
      }

      handleClose();
    } catch (e) { }
  }, [form, fileList, formData, modalType]);

  const beforeUploadDocument = async (file) => {
    const isFile = [
      '.csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/svg+xml',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ].includes(file.type);

    const isLt2M = file.size / 1024 / 1024 < 0.15;

    const isDocumentTYpe = form.getFieldValue('documentType') !== undefined;

    if (!isFile || !isLt2M || !isDocumentTYpe) {
      if (!isFile && !isLt2M) {
        errorDialog({
          title: <Text t="error" />,
          message: pleaseCheckFileFormatAndSize,
        });
      } else if (!isFile) {
        errorDialog({
          title: <Text t="error" />,
          message: pleaseSelectValidFile,
        });
      } else if (!isLt2M) {
        errorDialog({
          title: <Text t="error" />,
          message: fileSizeTooLarge,
        });
      } else if (!isDocumentTYpe) {
        errorDialog({
          title: <Text t="error" />,
          message: pleaseSelectDocumentType,
        });
      }
    }
    return (isFile && isLt2M && isDocumentTYpe) || Upload.LIST_IGNORE;
  };

  const handleDeleteFileList = useCallback((id, index) => {
    setDeletedFileList((state) => [...state, id]);
    setFileList((state) => state.filter((item) => item.uid !== id));
    setFormData((state) => state.filter((item, i) => i !== index));
  }, []);

  const onDocumentTypeChange = (value) => {
    form.setFieldsValue({
      documentType: value,
    });
  };

  const normFile = (e) => {
    const newFileList = e.fileList.filter((file) => {
      return !deletedFileList.includes(file.uid);
    });
    setFileList(newFileList);
    if (form.getFieldValue('documentType') !== undefined) {
      setFormData((state) => [
        ...state,
        {
          documentType: form.getFieldValue('documentType'),
        },
      ]);
    }
    form.resetFields(['documentType']);
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const filterModalType = modalType?.documentList?.filter(
    (item) => item?.value !== formData?.[0]?.documentType,
  );

  const customFormItem = () => {
    return (
      <CustomFormItem
        label={
          <div>
            <Text t="documentType" />
            <span>:</span>
          </div>
        }
        name="documentType"
      >
        <div>
          <CustomSelect
            id="companyInformation-generalCompanyInformation-formModal-documentType-select"
            height={36}
            style={{ width: 280 }}
            placeholder={<Text t="choose" />}
            onChange={onDocumentTypeChange}
          >
            {filterModalType?.map((item) => {
              return (
                <Option
                  id={`companyInformation-generalCompanyInformation-formModal-documentType-${item?.text}-option`}
                  key={item?.value}
                  value={item?.value}
                >
                  {item?.text}
                </Option>
              );
            })}
          </CustomSelect>
        </div>
      </CustomFormItem>
    );
  };

  return (
    <CustomModal
      className="invoice-title-modal"
      width={838}
      footer={false}
      maskClosable={false}
      title={modalType?.title}
      visible={modalVisible}
      onCancel={handleClose}
      closeIcon={<CustomImage src={modalClose} />}
    >
      <CustomForm
        name="deliveryAddressAddForm"
        className="modal-form"
        initialValues={{}}
        autoComplete="off"
        layout={'vertical'}
        form={form}
      >
        <div className="form-item">
          <CustomFormItem
            label={
              <div>
                <Text t={modalType?.label} />
              </div>
            }
            name="receiverSurName"
            rules={[{ required: true, message: <Text t="requiredField2" /> }]}
          >
            <CustomInput
              id="companyInformation-generalCompanyInformation-formModal-receiverSurName-input"
              height={36}
            />
          </CustomFormItem>
        </div>

        {modalType?.type !== 2 ? (
          <>
            <Alert
              message={
                <div className="alert-message-contain">
                  <CustomImage src={iconInfo} />{' '}
                  <div className="alert-message-content">
                    <p>
                      <Text t="inOrderToChangeYourTaxNumberOr" />
                    </p>
                    <div className="info-list">
                      {modalType?.type === 1 && (
                        <span className="info-item">
                          <Text t="tradeRegistryGazette2" />
                        </span>
                      )}
                      <span className="info-item">
                        <Text t="taxBoard" />
                      </span>
                    </div>
                    <p>
                      <Text t="documentTypesYouCanUploadAreMsWord" />
                    </p>
                  </div>
                </div>
              }
              banner
              closable
              showIcon={false}
              className="custom-alert-warning"
            />

            <div className="form-item">
              {fileList.length > 0 &&
                fileList.map((item, index) => {
                  return (
                    <CustomFormItem
                      key={item}
                      label={
                        <div>
                          <Text t="documentType" />
                          <span>:</span>
                        </div>
                      }
                      name={`documentType${index}`}
                    >
                      <div>
                        <CustomSelect
                          id="companyInformation-generalCompanyInformation-second-formModal-documentType-select"
                          showSearch
                          height={36}
                          disabled={true}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            turkishToLower(option.children).includes(turkishToLower(input))
                          }
                          value={formData[index]?.documentType}
                          placeholder={<Text t="choose" />}
                          className="custom-document-type-select"
                          style={{ width: 280 }}
                        >
                          {modalType?.documentList?.map((item) => {
                            return (
                              <Option
                                id={`companyInformation-generalCompanyInformation-second-formModal-documentType-${item?.text}-option`}
                                key={item?.value}
                                value={item?.value}
                              >
                                {item?.text}
                              </Option>
                            );
                          })}
                        </CustomSelect>
                        <span className="uploaded-file-name">
                          {item?.name}
                          <span
                            className="delete-btn"
                            onClick={() => handleDeleteFileList(item?.uid, index)}
                          >
                            <Text t="delete3" />
                          </span>
                        </span>
                      </div>
                    </CustomFormItem>
                  );
                })}

              {modalType?.type === 1 && fileList.length < 2 && customFormItem()}

              {modalType?.type !== 1 && fileList.length < 1 && customFormItem()}
            </div>

            <hr />

            <CustomFormItem name="file" getValueFromEvent={normFile}>
              <Upload
                name="excel"
                listType="picture-card"
                showUploadList={false}
                multiple={false}
                beforeUpload={(file) => {
                  if (modalType?.type !== 2) {
                    return beforeUploadDocument(file);
                  } else {
                    return;
                  }
                }}
              >
                <div className="file-uploader">
                  <CustomImage src={iconUpload} />
                  {isMobil === true ? (
                    <h5>
                      <Text t="uploadYourDocumentToThisArea" />
                    </h5>
                  ) : (
                    <h5>
                      <Text t="dragOrUploadYourDocumentToThisArea" />
                    </h5>
                  )}
                  <div className="mute-text">
                    .DOC, .DOCX, .XLS, .PPTX, .PDF, .JPG, .PNG, .TIFF - Maks.Boyut 150 KB
                  </div>
                  <div className="action-btn">
                    <CustomButton
                      id="companyInformation-generalCompanyInformation-formModal-add-btn"
                      className="add-btn"
                    >
                      <Text t="browse2" />
                    </CustomButton>
                  </div>
                </div>
              </Upload>
            </CustomFormItem>
          </>
        ) : null}

        <div className="footer-content">
          <span onClick={handleClose} className="cancel-button">
            <Text t="giveUp" />
          </span>
          <CustomButton
            id="companyInformation-generalCompanyInformation-formModal-save-button"
            onClick={onSave}
            className="save-button"
          >
            <Text t="saveChanges" />
          </CustomButton>
        </div>
      </CustomForm>
    </CustomModal>
  );
};

export default FormModal;
